/**
 * The file tree.
 *
 * WHAT MAKES IT FEEL FAST
 *   - The rows are DERIVED from the change log, so opening a project is a fold
 *     over data already in memory: no request, no spinner, no skeleton.
 *   - Only visible rows are rendered (virtualised), so a project with three
 *     thousand files costs the same as one with thirty.
 *   - Collapsed folders contribute one row, not their whole subtree, so the
 *     virtualiser's list is short to begin with.
 *   - Every interaction changes colour only, never layout, so nothing reflows
 *     under the pointer.
 *
 * DATA DEFINITION
 *   A Row is { node, depth } (from @lattice/core): one visible line, and how
 *   far to indent it. `visibleRows` decides which rows exist given which
 *   folders the person has expanded — so expanding a folder is a pure
 *   recomputation, not a mutation of a rendered list.
 */
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChevronRight, MessageSquare } from 'lucide-react'
import * as ContextMenu from '@radix-ui/react-context-menu'
import {
  visibleRows, type Row, type TreeNode, type ProjectId, type Path,
  fuzzyMatch, flattenTree, sortTree, compareNodes,
} from '@lattice/core'
import { useWorkspace, personOf, treesByProject, commentsByPath } from '@/store/workspace'
import { useUi, sortFor } from '@/store/ui'
import { FileIcon, FolderIcon } from './FileIcon'
import { Avatar, Tooltip, EmptyState } from './ui/primitives'
import { formatBytes, formatRelative, formatExact, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'

const ROW_HEIGHT = 26

export function FileTree({ projectId, onRename, onDelete, onDownload }: {
  projectId: ProjectId
  onRename: (path: string, currentName: string) => void
  onDelete: (paths: string[]) => void
  onDownload: (paths: string[]) => void
}) {
  const changes = useWorkspace(s => s.changes)
  const comments = useWorkspace(s => s.comments)
  const roster = useWorkspace(s => s.roster)
  const { expanded, toggleExpanded, openPath, open, selection, select, setAnchor, anchor, filter, setExpanded } = useUi()
  const sort = useUi(s => sortFor(s, projectId))

  const scrollRef = useRef<HTMLDivElement>(null)

  // treesByProject is memoised and shared, so the re-sort is a separate step
  // over the SAME folded tree: changing the order never re-folds the log.
  const tree = useMemo(
    () => sortTree(treesByProject(changes).get(projectId) ?? [], sort),
    [changes, projectId, sort],
  )
  const threads = useMemo(() => commentsByPath(comments), [comments])

  /**
   * When a filter is typed, show every MATCHING FILE as a flat list rather
   * than a tree. A tree whose folders must be opened to reveal a match is a
   * worse answer than a list of the matches themselves.
   *
   * RELEVANCE DELIBERATELY OUTRANKS THE SORT KEY HERE. Typing a filter is
   * asking a question, and the best answer belongs at the top; a list sorted
   * by size that happens to contain your file in nineteenth place has not
   * answered it. The chosen order still decides ties, so equally good matches
   * come back newest-first (or largest-first) as you asked.
   */
  const rows: Row[] = useMemo(() => {
    const query = filter.trim()
    if (!query) return visibleRows(tree, expanded)
    const matches = flattenTree(tree)
      .filter(n => n.type === 'file')
      .map(n => ({ node: n, score: fuzzyMatch(query, n.name)?.score ?? fuzzyMatch(query, n.path)?.score ?? -1 }))
      .filter(m => m.score >= 0)
      .sort((a, b) => b.score - a.score || compareNodes(a.node, b.node, sort))
      .slice(0, 400)
    return matches.map(m => ({ node: m.node, depth: 0 }))
  }, [tree, expanded, filter, sort])

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  })

  /** Keep the open file in view when it is chosen from elsewhere (palette, link). */
  useEffect(() => {
    if (!openPath) return
    const index = rows.findIndex(r => r.node.path === openPath)
    if (index >= 0) virtualizer.scrollToIndex(index, { align: 'auto' })
  }, [openPath, rows, virtualizer])

  const pathsOf = useCallback((node: TreeNode): string[] =>
    node.type === 'file' ? [node.path] : [node.path], [])

  /**
   * Clicking a row.
   * Plain click selects and opens; ⌘/Ctrl adds to the selection; Shift extends
   * from the last anchor — the three behaviours every file browser has, so
   * nobody has to learn ours.
   */
  const onRowClick = useCallback((e: React.MouseEvent, row: Row) => {
    const path = row.node.path
    if (e.shiftKey && anchor) {
      const from = rows.findIndex(r => r.node.path === anchor)
      const to = rows.findIndex(r => r.node.path === path)
      if (from >= 0 && to >= 0) {
        const [lo, hi] = from < to ? [from, to] : [to, from]
        select(rows.slice(lo, hi + 1).map(r => r.node.path), 'replace')
        return
      }
    }
    if (e.metaKey || e.ctrlKey) {
      select([path], 'toggle')
      setAnchor(path)
      return
    }
    select([path], 'replace')
    setAnchor(path)
    if (row.node.type === 'folder') toggleExpanded(path)
    else open(path)
  }, [rows, anchor, select, setAnchor, toggleExpanded, open])

  if (tree.length === 0) {
    return (
      <EmptyState
        title="No files yet"
        hint="Drop files here, or press ⌘U to choose them. Anything already in the workspace uploads instantly."
      />
    )
  }

  if (rows.length === 0) {
    return <EmptyState title="No matches" hint={`Nothing in this project matches “${filter}”.`} />
  }

  return (
    <div ref={scrollRef} className="h-full overflow-auto" role="tree" aria-label="Files">
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map(item => {
          const row = rows[item.index]
          const node = row.node
          const isOpen = openPath === node.path
          const isSelected = selection.has(node.path)
          const threadCount = (threads.get(`${projectId}|${node.path}`) ?? []).filter(c => !c.deletedAt).length

          return (
            <ContextMenu.Root key={node.path}>
              <ContextMenu.Trigger asChild>
                <div
                  role="treeitem"
                  aria-selected={isSelected}
                  aria-expanded={node.type === 'folder' ? expanded.has(node.path) : undefined}
                  onClick={e => onRowClick(e, row)}
                  onDoubleClick={() => node.type === 'file' && open(node.path)}
                  className={cn(
                    'snap absolute inset-x-0 flex items-center gap-1.5 pr-3 text-sm cursor-default select-none',
                    isOpen ? 'bg-[var(--accent-soft)]'
                      : isSelected ? 'bg-[var(--bg-active)]'
                      : 'hover:bg-[var(--bg-hover)]',
                  )}
                  style={{
                    height: ROW_HEIGHT,
                    transform: `translateY(${item.start}px)`,
                    paddingLeft: 8 + row.depth * 14,
                  }}
                >
                  {node.type === 'folder' ? (
                    <ChevronRight
                      className={cn('h-3 w-3 shrink-0 text-[var(--fg-subtle)] transition-transform duration-100',
                        expanded.has(node.path) && 'rotate-90')}
                      strokeWidth={2}
                    />
                  ) : (
                    <span className="w-3 shrink-0" />
                  )}

                  {node.type === 'folder'
                    ? <FolderIcon open={expanded.has(node.path)} />
                    : <FileIcon path={node.path} />}

                  <span className={cn('min-w-0 flex-1 truncate', isOpen && 'font-medium')}>
                    {node.name}
                    {filter && node.type === 'file' && node.path.includes('/') && (
                      <span className="ml-2 text-xs text-[var(--fg-subtle)]">{node.path.slice(0, node.path.lastIndexOf('/'))}</span>
                    )}
                  </span>

                  {threadCount > 0 && (
                    <Tooltip label={pluralise(threadCount, 'comment')}>
                      <span className="flex shrink-0 items-center gap-0.5 text-[10px] text-[var(--fg-subtle)]">
                        <MessageSquare className="h-3 w-3" strokeWidth={1.75} />{threadCount}
                      </span>
                    </Tooltip>
                  )}

                  {node.type === 'folder' ? (
                    <span className="tabular shrink-0 text-[11px] text-[var(--fg-subtle)]">{node.fileCount}</span>
                  ) : (
                    <>
                      <span className="tabular hidden shrink-0 text-[11px] text-[var(--fg-subtle)] sm:block">
                        {formatBytes(node.entry.size)}
                      </span>
                      <Tooltip label={formatExact(node.entry.updatedAt)}>
                        <span className="tabular hidden w-14 shrink-0 text-right text-[11px] text-[var(--fg-subtle)] md:block">
                          {formatRelative(node.entry.updatedAt)}
                        </span>
                      </Tooltip>
                      <Avatar user={personOf(roster, node.entry.updatedBy)} size={16} className="hidden md:inline-flex" />
                    </>
                  )}
                </div>
              </ContextMenu.Trigger>

              <ContextMenu.Portal>
                <ContextMenu.Content className="pop-in z-50 min-w-44 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)] p-1 shadow-pop">
                  <MenuItem onSelect={() => onDownload(selection.has(node.path) ? [...selection] : pathsOf(node))}>
                    Download{selection.size > 1 && selection.has(node.path) ? ` ${selection.size} items` : ''}
                  </MenuItem>
                  <MenuItem onSelect={() => onRename(node.path, node.name)}>Rename…</MenuItem>
                  {node.type === 'file' && <MenuItem onSelect={() => open(node.path)}>Open</MenuItem>}
                  {node.type === 'folder' && (
                    <MenuItem onSelect={() => setExpanded(expandSubtree(node, expanded))}>
                      Expand all
                    </MenuItem>
                  )}
                  <ContextMenu.Separator className="my-1 h-px bg-[var(--border)]" />
                  <MenuItem
                    danger
                    onSelect={() => onDelete(selection.has(node.path) ? [...selection] : pathsOf(node))}
                  >
                    Delete{selection.size > 1 && selection.has(node.path) ? ` ${selection.size} items` : ''}
                  </MenuItem>
                </ContextMenu.Content>
              </ContextMenu.Portal>
            </ContextMenu.Root>
          )
        })}
      </div>
    </div>
  )
}

function MenuItem({ children, onSelect, danger }: { children: React.ReactNode; onSelect: () => void; danger?: boolean }) {
  return (
    <ContextMenu.Item
      onSelect={onSelect}
      className={cn(
        'snap cursor-pointer rounded-[var(--radius-sm)] px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-[var(--bg-hover)]',
        danger && 'text-[var(--deleted)]',
      )}
    >
      {children}
    </ContextMenu.Item>
  )
}

/** Every folder path at or below a node, so "expand all" is one state change. */
function expandSubtree(node: TreeNode, current: ReadonlySet<string>): Set<string> {
  const next = new Set(current)
  const walk = (n: TreeNode): void => {
    if (n.type !== 'folder') return
    next.add(n.path)
    n.children.forEach(walk)
  }
  walk(node)
  return next
}

export type { Path }
